<script setup lang="ts">
import { Plus, Trash } from "@iconoir/vue";
import { useCrud } from "~/composables/use-crud";
import WorkspaceSelect from "~/components/selects/WorkspaceSelect.vue";
import ScopePicker from "./ScopePicker.vue";
import PageBuilder from "./PageBuilder.vue";
import { deburr } from "lodash-es";
import {
  AppKind,
  appSubscribableEventTypes,
  type AppRegistration as App,
  type AppPage,
  type AppScope,
} from "@artech-global/types";

defineOptions({ name: "AppFormModal" });

const emit = defineEmits<{
  (e: "saved"): void;
  (e: "created", secrets: { clientSecret: string; signingSecret: string }): void;
}>();

const { t } = useI18n();
const { $api } = useNuxtApp();
const toast = useToast();

const isOpen = ref(false);
const saving = ref(false);

const {
  formModel,
  formErrors,
  errorMsg,
  isCreate,
  saveLoading,
  save,
  load,
  reset,
  setErrors,
  clearErrors,
} = useCrud<App>({
  endpoint: "/apps",
  defaultFormModel: () =>
    ({
      id: "",
      slug: "",
      name: "",
      description: "",
      kind: AppKind.Integration,
      accessLevel: "public",
      allowedWorkspaceIDs: [] as string[],
      logoAssetID: "",
      tagline: "",
      highlights: [] as string[],
      screenshotAssetIDs: [] as string[],
      pricingNote: "",
      websiteURL: "",
      privacyURL: "",
      developerName: "",
      developerEmail: "",
      redirectURLs: [] as string[],
      requestedScopes: [] as AppScope[],
      webhookURL: "",
      eventTypes: [],
      embedURL: "",
      pages: [] as AppPage[],
      mcpEndpoint: "",
    }) as unknown as App,
});

const kindOptions = computed(() =>
  [AppKind.Integration, AppKind.PaymentGateway, AppKind.Function].map((value) => ({
    value,
    label: t(`app.kind.${value}`),
  })),
);

const accessLevelOptions = computed(() => [
  { value: "public", label: t("app.access.public") },
  { value: "private", label: t("app.access.private") },
]);

const eventTypeOptions = computed(() =>
  appSubscribableEventTypes.map((value) => ({ value, label: t(`app.eventType.${value}`) })),
);

const activeTab = ref("general");
const formBody = ref<HTMLElement | null>(null);
const tabItems = computed(() => [
  { value: "general", label: t("app.tab.general") },
  { value: "listing", label: t("app.tab.listing") },
  { value: "permissions", label: t("app.tab.permissions") },
  { value: "integration", label: t("app.tab.integration") },
]);

// A section is read from its first field: the body keeps the scroll offset of
// the section left behind otherwise.
watch(activeTab, () => formBody.value?.closest(".modal__body")?.scrollTo({ top: 0 }));

// The slug is a public identifier: keep it lowercase kebab-case as it is typed.
watch(
  () => (formModel as any).slug,
  (val) => {
    if (!val) return;
    const normalized = toKebab(String(val));
    if (normalized !== val) (formModel as any).slug = normalized;
  },
);

const MAX_PAGES = 8;
const MAX_HIGHLIGHTS = 6;
const MAX_SCREENSHOTS = 5;

function addHighlight() {
  const highlights = ((formModel as any).highlights ||= []) as string[];
  if (highlights.length < MAX_HIGHLIGHTS) highlights.push("");
}

function removeHighlight(i: number) {
  ((formModel as any).highlights as string[]).splice(i, 1);
}

function toKebab(val: string) {
  return deburr(val)
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-{2,}/g, "-");
}

async function open(id?: string) {
  reset();
  activeTab.value = "general";
  if (id) {
    try {
      await load(id);
    } catch {
      if (errorMsg.value) toast.error({ title: errorMsg.value });
      return;
    }
    // Omitted list fields come back absent; the tags/select inputs need arrays.
    for (const key of ["redirectURLs", "requestedScopes", "eventTypes", "pages", "highlights", "screenshotAssetIDs", "allowedWorkspaceIDs"]) {
      (formModel as any)[key] ||= [];
    }
  }
  isOpen.value = true;
}

async function submit() {
  if (!isCreate.value) {
    try {
      await save();
      isOpen.value = false;
      toast.success({ title: t("message.updateSuccess") });
      emit("saved");
    } catch {
      // Field + banner errors are already bound from useCrud.
    }
    return;
  }

  // Create is called directly: the one-time secrets only exist in this response.
  saving.value = true;
  clearErrors();
  try {
    const created = await $api<{ clientSecret: string; signingSecret: string }>("/apps", {
      method: "POST",
      body: { ...formModel },
    });
    isOpen.value = false;
    toast.success({ title: t("message.createSuccess") });
    emit("saved");
    emit("created", {
      clientSecret: created.clientSecret,
      signingSecret: created.signingSecret,
    });
  } catch (e: any) {
    setErrors(e);
  } finally {
    saving.value = false;
  }
}

defineExpose({ open });
</script>

<template>
  <Modal
    v-model:open="isOpen"
    :title="isCreate ? t('app.add') : t('app.edit')"
    size="xl"
    @close="isOpen = false"
  >
    <!-- The section switcher lives in the fixed band, so it stays put while a
         long section scrolls under it. -->
    <template #subheader>
      <Tabs v-model="activeTab" :items="tabItems" />
    </template>

    <div ref="formBody" class="app-form">
      <!-- General -->
      <template v-if="activeTab === 'general'">
        <div class="app-form__row">
          <FormField
            v-model="(formModel as any).name"
            :label="t('field.name')"
            :error="formErrors.name"
            required
          />
          <FormField
            v-model="(formModel as any).slug"
            :label="t('field.slug')"
            :help-text="t('app.slugNote')"
            :error="formErrors.slug"
            required
          />
        </div>

        <FormTextarea
          v-model="(formModel as any).description"
          :label="t('field.description')"
          :error="formErrors.description"
          :rows="3"
        />

        <div class="app-form__row">
          <FormSelect
            v-model="(formModel as any).kind"
            :label="t('app.field.kind')"
            :options="kindOptions"
          />
          <FormSelect
            v-model="(formModel as any).accessLevel"
            :label="t('app.field.accessLevel')"
            :help-text="t('app.accessLevelNote')"
            :options="accessLevelOptions"
          />
        </div>

        <WorkspaceSelect
          v-if="(formModel as any).accessLevel === 'private'"
          v-model="(formModel as any).allowedWorkspaceIDs"
          :label="t('app.field.allowedWorkspaces')"
          :error="formErrors.allowedWorkspaceIDs"
          multiple
        />

        <Divider />

        <span class="text-body-secondary content-neutral-strong">{{ t("app.developerTitle") }}</span>

        <div class="app-form__row">
          <FormField
            v-model="(formModel as any).developerName"
            :label="t('app.field.developerName')"
            :error="formErrors.developerName"
          />
          <FormField
            v-model="(formModel as any).developerEmail"
            :label="t('app.field.developerEmail')"
            :error="formErrors.developerEmail"
          />
        </div>
      </template>

      <!-- Listing -->
      <template v-else-if="activeTab === 'listing'">
        <p class="text-body-small-tertiary content-neutral-medium">{{ t("app.listingNote") }}</p>

        <AssetUpload
          v-model="(formModel as any).logoAssetID"
          :label="t('app.field.logo')"
          :error="formErrors.logoAssetID"
          accept="image/*"
          file-list="single"
        />

        <div class="app-form__row">
          <FormField
            v-model="(formModel as any).tagline"
            :label="t('app.field.tagline')"
            :help-text="t('app.taglineNote')"
            :error="formErrors.tagline"
          />
          <FormField
            v-model="(formModel as any).pricingNote"
            :label="t('app.field.pricingNote')"
            :help-text="t('app.pricingNoteNote')"
            :error="formErrors.pricingNote"
          />
        </div>

        <div class="app-form__row">
          <FormField
            v-model="(formModel as any).websiteURL"
            :label="t('app.field.websiteURL')"
            :error="formErrors.websiteURL"
          />
          <FormField
            v-model="(formModel as any).privacyURL"
            :label="t('app.field.privacyURL')"
            :error="formErrors.privacyURL"
          />
        </div>

        <div class="flex flex-col gap-xsmall">
          <span class="text-body-small-secondary">{{ t("app.field.highlights") }}</span>
          <div
            v-for="(_, i) in ((formModel as any).highlights as string[])"
            :key="'h' + i"
            class="flex gap-small items-start"
          >
            <FormField
              v-model="(formModel as any).highlights[i]"
              :error="formErrors[`highlights[${i}]`]"
              class="flex-1"
            />
            <ButtonIcon variant="text-neutral" size="small" @click="removeHighlight(i)">
              <Trash class="icon" />
            </ButtonIcon>
          </div>
          <Button
            variant="tertiary"
            size="small"
            class="w-fit"
            :disabled="((formModel as any).highlights?.length || 0) >= MAX_HIGHLIGHTS"
            @click="addHighlight"
          >
            <Plus class="icon" />
            {{ t("app.addHighlight") }}
          </Button>
        </div>

        <AssetUpload
          v-model="(formModel as any).screenshotAssetIDs"
          :label="t('app.field.screenshots')"
          :help-text="t('app.screenshotsNote')"
          :error="formErrors.screenshotAssetIDs"
          accept="image/*"
          file-list="gallery"
          multiple
          :max-files="MAX_SCREENSHOTS"
        />
      </template>

      <!-- Permissions -->
      <template v-else-if="activeTab === 'permissions'">
        <p class="text-body-small-tertiary content-neutral-medium">{{ t("app.scopesNote") }}</p>

        <ScopePicker v-model="(formModel as any).requestedScopes" />
      </template>

      <!-- Integration -->
      <template v-else>
        <span class="text-body-secondary content-neutral-strong">{{ t("app.webhookTitle") }}</span>

        <div class="app-form__row">
          <FormField
            v-model="(formModel as any).webhookURL"
            :label="t('app.field.webhookURL')"
            :help-text="t('app.webhookURLNote')"
            :error="formErrors.webhookURL"
          />
          <FormTagSelect
            v-model="(formModel as any).eventTypes"
            :label="t('app.field.eventTypes')"
            :options="eventTypeOptions"
            :help-text="t('app.eventTypesNote')"
            multiple
            searchable
          />
        </div>

        <FormTagsInput
          v-model="(formModel as any).redirectURLs"
          :label="t('app.field.redirectURLs')"
          :help-text="t('app.redirectURLsNote')"
          :error="formErrors.redirectURLs"
        />

        <Divider />

        <span class="text-body-secondary content-neutral-strong">{{ t("app.surfaceTitle") }}</span>

        <div class="app-form__row">
          <FormField
            v-model="(formModel as any).embedURL"
            :label="t('app.field.embedURL')"
            :help-text="t('app.embedURLNote')"
            :error="formErrors.embedURL"
          />
          <FormField
            v-model="(formModel as any).mcpEndpoint"
            :label="t('app.field.mcpEndpoint')"
            :help-text="t('app.mcpEndpointNote')"
            :error="formErrors.mcpEndpoint"
          />
        </div>

        <span class="text-body-secondary content-neutral-strong">{{ t("app.pagesTitle") }}</span>

        <PageBuilder
          v-model="(formModel as any).pages"
          :errors="formErrors"
          :max="MAX_PAGES"
        />
      </template>

      <InlineMessage v-if="errorMsg" variant="negative">{{ errorMsg }}</InlineMessage>
    </div>

    <template #actions>
      <Button variant="tertiary" size="medium" @click="isOpen = false">
        {{ t("action.cancel") }}
      </Button>
      <Button
        variant="primary"
        size="medium"
        :disabled="saveLoading || saving"
        @click="submit"
      >
        {{ t("action.save") }}
      </Button>
    </template>
  </Modal>
</template>

<style scoped lang="scss">
.app-form {
  display: flex;
  flex-direction: column;
  gap: token("gap-medium");

  // Two short fields per line: the form is read column by column, not as one
  // long stack.
  &__row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: start;
    gap: token("gap-medium");

    @include down("md") {
      grid-template-columns: 1fr;
    }
  }
}
</style>
